import { useQuery } from '@tanstack/react-query';
import { useSkateRelays } from '@/hooks/useSkateRelays';
import {
  SKATE_SPOT_KIND,
  SKATE_SHOP_KIND,
  CALENDAR_EVENT_KIND,
  SKATE_COACH_KIND,
  SKATE_FILMER_KIND,
  SKATE_EDITOR_KIND,
  SKATE_PHOTOGRAPHER_KIND,
  SKATE_BUILDER_KIND,
  SKATE_ARTIST_KIND,
  SKATE_GRAFFITI_KIND,
  SKATE_TATTOO_KIND,
  SKATE_TRAINER_KIND,
  SKATE_MUSICIAN_KIND,
  SKATE_JUDGE_KIND,
  SKATE_TOUR_KIND,
  SKATE_CREW_KIND,
} from '@/lib/types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MonthBucket {
  /** "Jan '25" */
  label: string;
  /** YYYY-MM for sorting */
  key: string;
  spots: number;
  shops: number;
  events: number;
  coaches: number;
  filmers: number;
  editors: number;
  photographers: number;
  builders: number;
  tours: number;
  crews: number;
  total: number;
  /** Unique pubkeys that published anything this month */
  activeMappers: number;
}

export interface TopMapper {
  pubkey: string;
  spotCount: number;
  totalContent: number;
}

export interface GrowthStats {
  // ── Totals ──────────────────────────────────────────────────────────────
  totalSpots: number;
  totalShops: number;
  totalEvents: number;
  totalCoaches: number;
  totalFilmers: number;
  totalEditors: number;
  totalPhotographers: number;
  totalBuilders: number;
  // Additional Lab crafts
  totalArtists: number;
  totalGraffiti: number;
  totalTattoo: number;
  totalTrainers: number;
  totalMusicians: number;
  totalJudges: number;
  totalTours: number;
  totalCrews: number;
  /** All unique pubkeys that have published any content */
  totalMappers: number;

  // ── Trusted counts (pubkeys with ≥2 published items — organic community members) ──
  /** Spots from pubkeys with ≥2 total published items (low-effort spam filter) */
  trustedSpots: number;
  /** Unique pubkeys with ≥2 total published items */
  trustedMappers: number;

  // ── Time series ─────────────────────────────────────────────────────────
  /** Monthly buckets oldest → newest */
  monthly: MonthBucket[];
  /** Cumulative spots per month (for line chart) */
  cumulativeSpots: { key: string; label: string; value: number }[];

  // ── Leaderboard ─────────────────────────────────────────────────────────
  topMappers: TopMapper[];
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toMonthKey(ts: number): string {
  const d = new Date(ts * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function toMonthLabel(key: string): string {
  const [y, m] = key.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m) - 1]} '${y.slice(2)}`;
}

function fillMonthGaps(keys: string[]): string[] {
  if (keys.length === 0) return [];
  const sorted = [...keys].sort();
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const result: string[] = [];
  let [y, m] = first.split('-').map(Number);
  const [ey, em] = last.split('-').map(Number);
  while (y < ey || (y === ey && m <= em)) {
    result.push(`${y}-${String(m).padStart(2, '0')}`);
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGrowthStats() {
  // Community stats read from the CANONICAL community relay (goskatespots) only,
  // so the numbers always match the authoritative community dataset — not a
  // blend of the user's personal relays that would drift per-device.
  const { queryCanonical } = useSkateRelays();
  const querySkateContent = queryCanonical;

  return useQuery<GrowthStats>({
    queryKey: ['growth-stats', 'canonical'],
    queryFn: async () => {
      const signal = AbortSignal.timeout(15000);

      // Query all content types in parallel from the community relay.
      // High limits to get full history — this is a stats page, not a feed.
      const [
        spotEvents,
        shopEvents,
        calendarEvents,
        coachEvents,
        filmerEvents,
        editorEvents,
        photographerEvents,
        builderEvents,
        tourEvents,
        crewEvents,
        artistEvents,
        graffitiEvents,
        tattooEvents,
        trainerEvents,
        musicianEvents,
        judgeEvents,
      ] = await Promise.all([
        querySkateContent([{ kinds: [SKATE_SPOT_KIND], '#t': ['skatespot'], limit: 2000 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_SHOP_KIND], limit: 2000 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [CALENDAR_EVENT_KIND], '#t': ['skateboarding'], limit: 2000 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_COACH_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_FILMER_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_EDITOR_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_PHOTOGRAPHER_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_BUILDER_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_TOUR_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_CREW_KIND], limit: 500 }], { signal }).catch(() => []),
        // All other Lab crafts, each counted individually so the stats page can
        // show a card per craft (matching what /lab lists).
        querySkateContent([{ kinds: [SKATE_ARTIST_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_GRAFFITI_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_TATTOO_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_TRAINER_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_MUSICIAN_KIND], limit: 500 }], { signal }).catch(() => []),
        querySkateContent([{ kinds: [SKATE_JUDGE_KIND], limit: 500 }], { signal }).catch(() => []),
      ]);

      // Deduplicate addressable events (keep newest per pubkey+kind+d)
      function dedup(events: typeof spotEvents) {
        const map = new Map<string, typeof events[0]>();
        for (const e of events) {
          const d = e.tags.find(t => t[0] === 'd')?.[1] ?? e.id;
          const key = `${e.pubkey}:${e.kind}:${d}`;
          const existing = map.get(key);
          if (!existing || e.created_at > existing.created_at) map.set(key, e);
        }
        return Array.from(map.values());
      }

      const spots   = dedup(spotEvents);
      const shops   = dedup(shopEvents);
      const events  = dedup(calendarEvents);
      const coaches = dedup(coachEvents);
      const filmers = dedup(filmerEvents);
      const editors = dedup(editorEvents);
      const photographers = dedup(photographerEvents);
      const builders = dedup(builderEvents);
      const tours   = dedup(tourEvents);
      const crews   = dedup(crewEvents);
      const artists      = dedup(artistEvents);
      const graffiti     = dedup(graffitiEvents);
      const tattoo       = dedup(tattooEvents);
      const trainers     = dedup(trainerEvents);
      const musicians    = dedup(musicianEvents);
      const judges       = dedup(judgeEvents);
      // Aggregate of the "other" crafts for active-mapper / total rollups.
      const otherLab = [...artists, ...graffiti, ...tattoo, ...trainers, ...musicians, ...judges];

      // ── Monthly bucketing ───────────────────────────────────────────────

      const bucketMap = new Map<string, {
        spots: Set<string>; shops: Set<string>; events: Set<string>;
        coaches: Set<string>; filmers: Set<string>; editors: Set<string>;
        photographers: Set<string>; builders: Set<string>;
        tours: Set<string>; crews: Set<string>; pubkeys: Set<string>;
      }>();

      function ensureBucket(key: string) {
        if (!bucketMap.has(key)) {
          bucketMap.set(key, {
            spots: new Set(), shops: new Set(), events: new Set(),
            coaches: new Set(), filmers: new Set(), editors: new Set(),
            photographers: new Set(), builders: new Set(),
            tours: new Set(), crews: new Set(), pubkeys: new Set(),
          });
        }
        return bucketMap.get(key)!;
      }

      for (const e of spots)   { const b = ensureBucket(toMonthKey(e.created_at)); b.spots.add(e.id);   b.pubkeys.add(e.pubkey); }
      for (const e of shops)   { const b = ensureBucket(toMonthKey(e.created_at)); b.shops.add(e.id);   b.pubkeys.add(e.pubkey); }
      for (const e of events)  { const b = ensureBucket(toMonthKey(e.created_at)); b.events.add(e.id);  b.pubkeys.add(e.pubkey); }
      for (const e of coaches) { const b = ensureBucket(toMonthKey(e.created_at)); b.coaches.add(e.id); b.pubkeys.add(e.pubkey); }
      for (const e of filmers) { const b = ensureBucket(toMonthKey(e.created_at)); b.filmers.add(e.id); b.pubkeys.add(e.pubkey); }
      for (const e of editors) { const b = ensureBucket(toMonthKey(e.created_at)); b.editors.add(e.id); b.pubkeys.add(e.pubkey); }
      for (const e of photographers) { const b = ensureBucket(toMonthKey(e.created_at)); b.photographers.add(e.id); b.pubkeys.add(e.pubkey); }
      for (const e of builders) { const b = ensureBucket(toMonthKey(e.created_at)); b.builders.add(e.id); b.pubkeys.add(e.pubkey); }
      for (const e of tours)   { const b = ensureBucket(toMonthKey(e.created_at)); b.tours.add(e.id);   b.pubkeys.add(e.pubkey); }
      for (const e of crews)   { const b = ensureBucket(toMonthKey(e.created_at)); b.crews.add(e.id);   b.pubkeys.add(e.pubkey); }
      // Other Lab crafts contribute to active-mapper counts (no dedicated series).
      for (const e of otherLab) { const b = ensureBucket(toMonthKey(e.created_at)); b.pubkeys.add(e.pubkey); }

      // Fill any gaps between first and last month so charts have continuous X axis
      const allKeys = fillMonthGaps(Array.from(bucketMap.keys()));

      const monthly: MonthBucket[] = allKeys.map(key => {
        const b = bucketMap.get(key);
        const s  = b?.spots.size   ?? 0;
        const sh = b?.shops.size   ?? 0;
        const ev = b?.events.size  ?? 0;
        const co = b?.coaches.size ?? 0;
        const fi = b?.filmers.size ?? 0;
        const ed = b?.editors.size ?? 0;
        const ph = b?.photographers.size ?? 0;
        const bu = b?.builders.size ?? 0;
        const to = b?.tours.size   ?? 0;
        const cr = b?.crews.size   ?? 0;
        return {
          label: toMonthLabel(key),
          key,
          spots: s, shops: sh, events: ev,
          coaches: co, filmers: fi, editors: ed,
          photographers: ph, builders: bu, tours: to, crews: cr,
          total: s + sh + ev + co + fi + ed + ph + bu + to + cr,
          activeMappers: b?.pubkeys.size ?? 0,
        };
      });

      // Cumulative spots line chart data
      let running = 0;
      const cumulativeSpots = monthly.map(m => {
        running += m.spots;
        return { key: m.key, label: m.label, value: running };
      });

      // ── Top mappers by spot count ────────────────────────────────────────

      const mapperSpots = new Map<string, number>();
      const mapperTotal = new Map<string, number>();

      for (const e of spots)   { mapperSpots.set(e.pubkey, (mapperSpots.get(e.pubkey) ?? 0) + 1); }
      const allContent = [...spots, ...shops, ...events, ...coaches, ...filmers, ...editors, ...photographers, ...builders, ...tours, ...crews, ...otherLab];
      for (const e of allContent) { mapperTotal.set(e.pubkey, (mapperTotal.get(e.pubkey) ?? 0) + 1); }

      const topMappers: TopMapper[] = Array.from(mapperSpots.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([pubkey, spotCount]) => ({
          pubkey,
          spotCount,
          totalContent: mapperTotal.get(pubkey) ?? spotCount,
        }));

      // ── Unique mappers ───────────────────────────────────────────────────

      const allPubkeys = new Set(allContent.map(e => e.pubkey));

      // ── Trusted counts ───────────────────────────────────────────────────
      // "Trusted" = pubkey that has published ≥2 distinct items.
      // This is a lightweight heuristic: spam accounts typically publish 1 fake
      // event and stop, while real community members publish multiple items over time.
      // No extra relay queries needed — we already have all the content above.

      const pubkeyItemCount = new Map<string, number>();
      for (const e of allContent) {
        pubkeyItemCount.set(e.pubkey, (pubkeyItemCount.get(e.pubkey) ?? 0) + 1);
      }
      const isTrustedPubkey = (pk: string) => (pubkeyItemCount.get(pk) ?? 0) >= 2;

      const trustedSpots   = spots.filter(e => isTrustedPubkey(e.pubkey)).length;
      const trustedMappers = Array.from(allPubkeys).filter(isTrustedPubkey).length;

      return {
        totalSpots:   spots.length,
        totalShops:   shops.length,
        totalEvents:  events.length,
        totalCoaches: coaches.length,
        totalFilmers: filmers.length,
        totalEditors: editors.length,
        totalPhotographers: photographers.length,
        totalBuilders: builders.length,
        totalArtists:   artists.length,
        totalGraffiti:  graffiti.length,
        totalTattoo:    tattoo.length,
        totalTrainers:  trainers.length,
        totalMusicians: musicians.length,
        totalJudges:    judges.length,
        totalTours:   tours.length,
        totalCrews:   crews.length,
        totalMappers: allPubkeys.size,
        trustedSpots,
        trustedMappers,
        monthly,
        cumulativeSpots,
        topMappers,
      };
    },
    staleTime: 15 * 60 * 1000, // 15 min — stats don't need to be live
    gcTime:    60 * 60 * 1000, // 1 hr in memory
    retry: 1,
  });
}


